import * as React from 'react';
import type { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import type { StoredComposerDraft } from './useComposerDraft';
import type { ComposerReplyType } from './types';

interface RecipientFields {
	bccValue: string;
	ccValue: string;
	showBcc: boolean;
	showCc: boolean;
}

const EMPTY_FIELDS: RecipientFields = { bccValue: '', ccValue: '', showBcc: false, showCc: false };

const applyUpdate = <T>(update: React.SetStateAction<T>, previous: T): T =>
	typeof update === 'function' ? (update as (prior: T) => T)(previous) : update;

const excludeRecipientFromCcValue = (ccValue: string, recipientEmail?: string): string => {
	const normalizedRecipient = recipientEmail?.trim().toLowerCase();
	return ccValue
		.split(',')
		.map(email => email.trim())
		.filter(email => email.length > 0 && email.toLowerCase() !== normalizedRecipient)
		.join(', ');
};

interface UseComposerRecipientsOptions {
	initialDraft: StoredComposerDraft | null;
	replyType: ComposerReplyType;
	ticket: ISupportTicket;
}

interface ComposerRecipients {
	bccValue: string;
	ccValue: string;
	setBccValue: React.Dispatch<React.SetStateAction<string>>;
	setCcValue: React.Dispatch<React.SetStateAction<string>>;
	setShowBcc: React.Dispatch<React.SetStateAction<boolean>>;
	setShowCc: React.Dispatch<React.SetStateAction<boolean>>;
	showBcc: boolean;
	showCc: boolean;
}

/**
 * Cc/Bcc held separately for the conversation (reply/note) and for a forward. A reply joins the
 * thread so it carries the thread's Cc list; a forward goes to someone the thread has never seen,
 * and one shared field mailed them the whole original Cc list.
 */
export const useComposerRecipients = ({
	initialDraft,
	replyType,
	ticket,
}: UseComposerRecipientsOptions): ComposerRecipients => {
	const isForward = replyType === 'forward';
	const draftIsForward = initialDraft?.replyType === 'forward';
	const contactEmail = ticket.contactEmail;

	const [conversationFields, setConversationFields] = React.useState<RecipientFields>(() => {
		if (!initialDraft || draftIsForward) return EMPTY_FIELDS;
		const ccValue = excludeRecipientFromCcValue(initialDraft.ccValue, contactEmail);
		return { bccValue: initialDraft.bccValue, ccValue, showBcc: !!initialDraft.bccValue, showCc: !!ccValue };
	});
	const [forwardFields, setForwardFields] = React.useState<RecipientFields>(() => {
		if (!initialDraft || !draftIsForward) return EMPTY_FIELDS;
		return {
			bccValue: initialDraft.bccValue,
			ccValue: initialDraft.ccValue,
			showBcc: !!initialDraft.bccValue,
			showCc: !!initialDraft.ccValue,
		};
	});

	// Bound to the rendered mode, not read at call time: useComposerSend clears these after its send
	// resolves, by which point the agent may have switched tabs.
	const fields = isForward ? forwardFields : conversationFields;
	const setFields = isForward ? setForwardFields : setConversationFields;

	const setCcValue: React.Dispatch<React.SetStateAction<string>> = update =>
		setFields(previous => ({ ...previous, ccValue: applyUpdate(update, previous.ccValue) }));
	const setBccValue: React.Dispatch<React.SetStateAction<string>> = update =>
		setFields(previous => ({ ...previous, bccValue: applyUpdate(update, previous.bccValue) }));
	const setShowCc: React.Dispatch<React.SetStateAction<boolean>> = update =>
		setFields(previous => ({ ...previous, showCc: applyUpdate(update, previous.showCc) }));
	const setShowBcc: React.Dispatch<React.SetStateAction<boolean>> = update =>
		setFields(previous => ({ ...previous, showBcc: applyUpdate(update, previous.showBcc) }));

	const ccSeeded = React.useRef(!draftIsForward && !!initialDraft?.ccValue);
	React.useEffect(() => {
		if (ccSeeded.current || !ticket.ccEmails?.length) return;
		ccSeeded.current = true;
		const ccValue = excludeRecipientFromCcValue(ticket.ccEmails.join(', '), contactEmail);
		if (!ccValue) return;
		// The ticket query polls, so this can land after the agent has typed their own Cc.
		setConversationFields(previous => (previous.ccValue ? previous : { ...previous, ccValue, showCc: true }));
	}, [contactEmail, ticket.ccEmails]);

	return { ...fields, setBccValue, setCcValue, setShowBcc, setShowCc };
};
